import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarClock, Save } from "lucide-react";
import { toast } from "sonner";

// Lets HR carry an employee's paper-register leave balance into the portal, once per leave type.
// The figure entered is the employee's ACTUAL available balance as on the given date — it is the
// baseline the portal starts from, NOT an addition to what the system has already accrued this
// year. From that date onward the monthly accrual builds on it and approved leave deducts from it.
// See backend routes_leave.py compute_balances().
export default function AdminEmployeeLeave({ employee }) {
  const eid = employee.id;
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [edits, setEdits] = useState({});
  const [ov, setOv] = useState({ month: today.slice(0, 7), extra_days: "", reason: "" });

  const { data: balances, isLoading } = useQuery({
    queryKey: ["empBalances", eid],
    queryFn: async () => (await api.get(`/employees/${eid}/leave-balances`)).data,
  });
  const { data: openings } = useQuery({
    queryKey: ["openingBalances", eid],
    queryFn: async () => (await api.get(`/employees/${eid}/leave-opening-balances`)).data,
  });

  const { data: overrides } = useQuery({
    queryKey: ["lwpOverrides", eid],
    queryFn: async () => (await api.get(`/employees/${eid}/lwp-limit-overrides`)).data,
  });
  const saveOv = useMutation({
    mutationFn: async (body) => (await api.put(`/employees/${eid}/lwp-limit-overrides`, body)).data,
    onSuccess: () => {
      toast.success("Unpaid-leave limit updated");
      qc.invalidateQueries({ queryKey: ["lwpOverrides", eid] });
      setOv((o) => ({ ...o, extra_days: "", reason: "" }));
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const save = useMutation({
    mutationFn: async ({ code, value, asOf }) =>
      (await api.put(`/employees/${eid}/leave-opening-balances`,
        { leave_type_code: code, opening_balance: Number(value) || 0, as_of: asOf })).data,
    onSuccess: (_d, { code }) => {
      toast.success("Balance carried over");
      qc.invalidateQueries({ queryKey: ["empBalances", eid] });
      qc.invalidateQueries({ queryKey: ["openingBalances", eid] });
      setEdits((e) => { const n = { ...e }; delete n[code]; return n; });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading || !balances) return <Loading />;

  const openingMeta = Object.fromEntries((openings || []).map((o) => [o.leave_type_code, o]));
  const applicable = balances.filter((b) => !b.lwp && b.code !== "COMP");
  const unpaid = balances.filter((b) => b.lwp).reduce((s, b) => s + (b.taken || 0) + (b.pending || 0), 0);

  if (applicable.length === 0) {
    return (
      <EmptyState icon={CalendarClock} title="No balance-tracked leave types apply"
        hint="This employee's category has no accrual-based leave types configured yet — load them from Leave Administration → Leave Types." />
    );
  }

  return (
    <Card className="border-slate-200 shadow-none overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <p className="text-sm text-navy font-medium">Carry over balance from the register</p>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          Enter the leave this employee actually has left, exactly as it stands in the paper register, and
          the date that figure is correct as on. Available becomes that number — it is the starting point,
          not an addition. The monthly accrual then builds on it from the next month, and any leave approved
          on or after that date is deducted automatically. Edit again only to correct a mistake.
        </p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="overline text-slate-500">Type</TableHead>
            <TableHead className="overline text-slate-500">Balance in register</TableHead>
            <TableHead className="overline text-slate-500">As on</TableHead>
            <TableHead className="overline text-slate-500">Accrued since</TableHead>
            <TableHead className="overline text-slate-500">Taken</TableHead>
            <TableHead className="overline text-slate-500">Pending</TableHead>
            <TableHead className="overline text-slate-500">Available</TableHead>
            <TableHead className="text-right overline text-slate-500"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {applicable.map((b) => {
              const meta = openingMeta[b.code];
              const edit = edits[b.code];
              const value = edit?.value ?? String(b.opening_balance ?? 0);
              // opening_as_of is blank once a baseline has lapsed into a new leave year - start fresh at today.
              const asOf = edit?.asOf ?? (b.opening_as_of || today);
              const dirty = !!edit && (Number(value) !== (b.opening_balance ?? 0) || asOf !== (b.opening_as_of || today));
              const setEdit = (patch) =>
                setEdits((prev) => ({ ...prev, [b.code]: { value, asOf, ...prev[b.code], ...patch } }));
              return (
                <TableRow key={b.code} className="table-row-i" data-testid={`leave-opening-row-${b.code}`}>
                  <TableCell className="font-medium text-navy">
                    {b.code}<span className="block text-xs text-slate-400 font-normal">{b.name}</span>
                  </TableCell>
                  <TableCell>
                    <Input type="number" step="0.5" min="0" className="w-24"
                      value={value}
                      onChange={(e) => setEdit({ value: e.target.value })}
                      data-testid={`opening-input-${b.code}`} />
                    {meta && (
                      <span className="block text-[10px] text-slate-400 mt-1">
                        Set by {meta.set_by}{meta.updated_by && meta.updated_by !== meta.set_by ? `, corrected by ${meta.updated_by}` : ""}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input type="date" max={today} className="w-40"
                      value={asOf}
                      onChange={(e) => setEdit({ asOf: e.target.value })}
                      data-testid={`opening-asof-${b.code}`} />
                  </TableCell>
                  <TableCell className="text-slate-600">{b.accrued}</TableCell>
                  <TableCell className="text-slate-600">{b.taken}</TableCell>
                  <TableCell className="text-slate-600">{b.pending}</TableCell>
                  <TableCell className="font-semibold text-navy">{b.available}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant={dirty ? "default" : "outline"} className={dirty ? "bg-navy hover:bg-navy-700" : ""}
                      disabled={save.isPending}
                      onClick={() => save.mutate({ code: b.code, value, asOf })}
                      data-testid={`save-opening-${b.code}`}>
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {unpaid > 0 && (
        <div className="px-4 py-3 border-t border-slate-100 text-sm" data-testid="unpaid-leave-summary">
          <span className="text-slate-600">Unpaid leave (LWP) this year: </span>
          <span className="font-semibold text-red-600">-{unpaid}</span>
          <span className="text-xs text-slate-500"> day(s) — deducted from salary in payroll, not from PL.</span>
        </div>
      )}
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50" data-testid="lwp-limit-override">
        <p className="text-sm text-navy font-medium">Unpaid leave limit: 5 days per month</p>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          The employee cannot apply for more than 5 unpaid days in a month. A month with more than 5 unpaid days earns
          no PL. For a genuine case, allow extra unpaid days for one month below (recorded in the Audit Trail).
        </p>
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <Input type="month" className="w-40 bg-white" value={ov.month}
            onChange={(e) => setOv({ ...ov, month: e.target.value })} data-testid="lwp-ov-month" />
          <Input type="number" min="0" max="31" step="0.5" placeholder="Extra days" className="w-28 bg-white"
            value={ov.extra_days} onChange={(e) => setOv({ ...ov, extra_days: e.target.value })} data-testid="lwp-ov-days" />
          <Input placeholder="Reason (required)" className="w-64 bg-white" value={ov.reason}
            onChange={(e) => setOv({ ...ov, reason: e.target.value })} data-testid="lwp-ov-reason" />
          <Button size="sm" className="bg-navy hover:bg-navy-700" disabled={saveOv.isPending || !ov.month || ov.extra_days === ""}
            onClick={() => saveOv.mutate({ month: ov.month, extra_days: Number(ov.extra_days) || 0, reason: ov.reason })}
            data-testid="lwp-ov-save">
            <Save className="h-3.5 w-3.5" />
          </Button>
        </div>
        {(overrides || []).length > 0 && (
          <ul className="mt-3 space-y-1">
            {overrides.map((o) => (
              <li key={o.month} className="text-xs text-slate-600">
                {o.month}: limit {5 + o.extra_days} days (+{o.extra_days}) — {o.reason} · by {o.set_by}
                <button className="ml-2 text-red-600 hover:underline" disabled={saveOv.isPending}
                  onClick={() => saveOv.mutate({ month: o.month, extra_days: 0, reason: "Removed" })}>remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
